/**
 * ContentEditorForm
 * Unified create/edit form used by dashboard collection pages.
 *
 * Why this exists:
 * - Keeps one form pipeline for `articles`, `animations`, and `models`.
 * - Emits a normalized payload + selected files to parent page.
 * - Parent page owns persistence and upload orchestration.
 */
import { useEffect, useState } from 'react';
import { listAuthorCreators } from '../../services/authorCreatorService';
import { getContentConfig, getEmptyContent } from '../../utils/contentConfig';
import { slugify } from '../../utils/slugify';

const EMPTY_FILES = {
  coverImageFile: null,
  relatedMediaFiles: [],
  articleSectionImageFiles: [],
  mediaFile: null,
  courseVideoFile: null,
  thumbnailFile: null,
  previewImageFile: null,
  attachmentFiles: [],
};

const EMPTY_ARTICLE_SECTION = {
  contentType: 'Text',
  subHeadline: '',
  body: '',
  imageUrl: '',
  imagePath: '',
  imageCaption: '',
};

const EMPTY_ARTICLE_REFERENCE = {
  text: '',
  link: '',
};

const EMPTY_ARTICLE_FOOTNOTE = {
  footnoteNumber: '',
  text: '',
  link: '',
};

const GALLERY_MEDIA_TYPE_OPTIONS = ['Video', 'Image', 'GIF'];

function normalizeForForm(collectionKey, content) {
  const base = getEmptyContent(collectionKey);
  if (!content) {
    return base;
  }

  // Merge persisted document into collection defaults for stable form state.
  const merged = { ...base, ...content };
  const articleSections = Array.isArray(merged.articleSections)
    ? merged.articleSections.map((section) => ({
        contentType: section?.contentType || 'Text',
        subHeadline: section?.subHeadline || '',
        body: section?.body || '',
        imageUrl: section?.imageUrl || '',
        imagePath: section?.imagePath || '',
        imageCaption: section?.imageCaption || '',
      }))
    : [];
  const articleReferences = Array.isArray(merged.articleReferences)
    ? merged.articleReferences.map((reference) => ({
        text: reference?.text || '',
        link: reference?.link || '',
      }))
    : [];
  const articleFootnotes = Array.isArray(merged.articleFootnotes)
    ? merged.articleFootnotes.map((footnote, index) => ({
        footnoteNumber:
          footnote?.footnoteNumber !== undefined
            ? String(footnote.footnoteNumber)
            : String(index + 1),
        text: footnote?.text || '',
        link: footnote?.link || '',
      }))
    : [];
  const normalizedAuthor =
    collectionKey === 'articles'
      ? (merged.author || '').toString().trim() || 'No Author'
      : merged.author;
  const normalizedAuthorCreatorId =
    collectionKey === 'articles'
      ? (merged.authorCreatorId || '').toString().trim()
      : merged.authorCreatorId;

  return {
    ...merged,
    author: normalizedAuthor,
    authorCreatorId: normalizedAuthorCreatorId,
    sortOrder:
      collectionKey === 'models'
        ? merged.sortOrder !== undefined && merged.sortOrder !== null
          ? String(merged.sortOrder)
          : ''
        : merged.sortOrder,
    tags: Array.isArray(merged.tags) ? merged.tags.join(', ') : merged.tags || '',
    articleSections,
    articleReferences,
    articleFootnotes,
    metadata:
      collectionKey === 'models'
        ? JSON.stringify(merged.metadata || {}, null, 2)
        : merged.metadata,
  };
}

export default function ContentEditorForm({
  collectionKey,
  initialContent,
  isSaving,
  onCancel,
  onSubmit,
}) {
  const config = getContentConfig(collectionKey);
  const [formValues, setFormValues] = useState(
    normalizeForForm(collectionKey, initialContent)
  );
  const [files, setFiles] = useState(EMPTY_FILES);
  const [errorMessage, setErrorMessage] = useState('');
  const [authorCreators, setAuthorCreators] = useState([]);
  const [authorCreatorsError, setAuthorCreatorsError] = useState('');

  useEffect(() => {
    // Reset form and staged files whenever collection or selected record changes.
    setFormValues(normalizeForForm(collectionKey, initialContent));
    setFiles(EMPTY_FILES);
    setErrorMessage('');
  }, [collectionKey, initialContent]);

  useEffect(() => {
    let isSubscribed = true;

    if (collectionKey !== 'articles') {
      setAuthorCreators([]);
      setAuthorCreatorsError('');
      return () => {
        isSubscribed = false;
      };
    }

    async function loadAuthorCreators() {
      setAuthorCreatorsError('');

      try {
        const result = await listAuthorCreators();
        if (!isSubscribed) {
          return;
        }

        setAuthorCreators(result);
      } catch (error) {
        if (!isSubscribed) {
          return;
        }

        setAuthorCreators([]);
        setAuthorCreatorsError(error.message ?? 'Failed to load staff profiles.');
      }
    }

    loadAuthorCreators();

    return () => {
      isSubscribed = false;
    };
  }, [collectionKey]);

  function updateField(fieldName, value) {
    setFormValues((current) => ({ ...current, [fieldName]: value }));
  }

  function addListItem(fieldName, nextItem) {
    setFormValues((current) => ({
      ...current,
      [fieldName]: [...(current[fieldName] || []), nextItem],
    }));
  }

  function removeListItem(fieldName, index) {
    setFormValues((current) => ({
      ...current,
      [fieldName]: (current[fieldName] || []).filter((_, itemIndex) => itemIndex !== index),
    }));

    if (fieldName === 'articleSections') {
      setFiles((current) => ({
        ...current,
        articleSectionImageFiles: (current.articleSectionImageFiles || []).filter(
          (_, itemIndex) => itemIndex !== index
        ),
      }));
    }
  }

  function updateListItem(fieldName, index, key, value) {
    setFormValues((current) => ({
      ...current,
      [fieldName]: (current[fieldName] || []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item
      ),
    }));
  }

  function handleTitleBlur() {
    if (!formValues.slug.trim() && formValues.title.trim()) {
      updateField('slug', slugify(formValues.title));
    }
  }

  function handleFileChange(fieldName, event, multiple = false) {
    const selected = event.target.files;
    setFiles((current) => ({
      ...current,
      [fieldName]: multiple ? Array.from(selected || []) : selected?.[0] || null,
    }));
  }

  function handleArticleSectionImageChange(index, event) {
    const selectedFile = event.target.files?.[0] || null;
    setFiles((current) => {
      const nextSectionFiles = [...(current.articleSectionImageFiles || [])];
      nextSectionFiles[index] = selectedFile;
      return {
        ...current,
        articleSectionImageFiles: nextSectionFiles,
      };
    });
  }

  function handleAuthorCreatorChange(authorCreatorId) {
    const selectedAuthorCreator = authorCreators.find(
      (authorCreator) => authorCreator.id === authorCreatorId
    );

    setFormValues((current) => ({
      ...current,
      authorCreatorId,
      author: selectedAuthorCreator?.name || 'No Author',
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setErrorMessage('');

    if (!formValues.title.trim()) {
      setErrorMessage('Title is required.');
      return;
    }

    let metadata = {};
    if (collectionKey === 'models' && formValues.metadata.trim()) {
      try {
        metadata = JSON.parse(formValues.metadata);
      } catch (error) {
        setErrorMessage('Metadata must be valid JSON.');
        return;
      }
    }

    const payload = {
      title: formValues.title,
      slug: formValues.slug,
      summary: formValues.summary,
      tags: formValues.tags,
      status: formValues.status,
    };

    // Collection-specific payload branching keeps write schema explicit.
    if (collectionKey === 'articles') {
      payload.authorCreatorId = (formValues.authorCreatorId || '').trim();
      payload.author = (formValues.author || '').trim() || 'No Author';
      payload.topic = formValues.topic;
      payload.articleLanguage = formValues.articleLanguage || 'en';
      payload.body = formValues.body;
      payload.articleSections = (formValues.articleSections || []).map((section) => ({
        contentType: section.contentType || 'Text',
        subHeadline: (section.subHeadline || '').trim(),
        body: (section.body || '').trim(),
        imageUrl: (section.imageUrl || '').trim(),
        imagePath: (section.imagePath || '').trim(),
        imageCaption: (section.imageCaption || '').trim(),
      }));
      payload.articleReferences = (formValues.articleReferences || []).map((reference) => ({
        text: (reference.text || '').trim(),
        link: (reference.link || '').trim(),
      }));
      payload.articleFootnotes = (formValues.articleFootnotes || []).map(
        (footnote, index) => ({
          footnoteNumber:
            Number.parseInt(footnote.footnoteNumber, 10) || index + 1,
          text: (footnote.text || '').trim(),
          link: (footnote.link || '').trim(),
        })
      );
    }

    if (collectionKey === 'animations') {
      payload.description = formValues.description;
      payload.topic = formValues.topic;
      payload.creatorName = formValues.creatorName;
      payload.creatorDiscipline = formValues.creatorDiscipline;
      payload.creatorStatement = formValues.creatorStatement;
      payload.mediaType = formValues.mediaType;
    }

    if (collectionKey === 'models') {
      payload.description = formValues.description;
      payload.type = formValues.type;
      payload.sortOrder = formValues.sortOrder;
      payload.metadata = metadata;
    }

    await onSubmit({
      id: initialContent?.id ?? null,
      payload,
      files,
    });
  }

  return (
    <form className="editor-form" onSubmit={handleSubmit}>
      <div className="editor-form-header">
        <h3>
          {initialContent ? 'Edit' : 'Create'} {config?.singular}
        </h3>
        {initialContent ? (
          <button type="button" className="button-link" onClick={onCancel}>
            New {config?.singular}
          </button>
        ) : null}
      </div>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}

      <label>
        Title
        <input
          value={formValues.title}
          onChange={(event) => updateField('title', event.target.value)}
          onBlur={handleTitleBlur}
          required
        />
      </label>

      <label>
        Slug
        <input
          value={formValues.slug}
          onChange={(event) => updateField('slug', event.target.value)}
          placeholder="auto-generated-from-title"
        />
      </label>

      <label>
        Summary
        <textarea
          rows={3}
          value={formValues.summary}
          onChange={(event) => updateField('summary', event.target.value)}
        />
      </label>

      <label>
        Tags (comma-separated)
        <input
          value={formValues.tags}
          onChange={(event) => updateField('tags', event.target.value)}
          placeholder="media literacy, workshop, youth"
        />
      </label>

      <label>
        Status
        <select
          value={formValues.status}
          onChange={(event) => updateField('status', event.target.value)}
        >
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </label>

      {collectionKey === 'articles' ? (
        <>
          <label>
            Staff profile
            <select
              value={formValues.authorCreatorId || ''}
              onChange={(event) => handleAuthorCreatorChange(event.target.value)}
            >
              <option value="">No Author</option>
              {authorCreators.map((authorCreator) => (
                <option key={authorCreator.id} value={authorCreator.id}>
                  {authorCreator.category
                    ? `${authorCreator.name} (${authorCreator.category})`
                    : authorCreator.name}
                </option>
              ))}
            </select>
          </label>

          {authorCreatorsError ? <p className="form-error">{authorCreatorsError}</p> : null}

          <label>
            Author display name
            <input value={formValues.author || 'No Author'} readOnly />
          </label>

          <label>
            Topic
            <input
              value={formValues.topic}
              onChange={(event) => updateField('topic', event.target.value)}
            />
          </label>

          <label>
            Language
            <select
              value={formValues.articleLanguage}
              onChange={(event) =>
                updateField('articleLanguage', event.target.value)
              }
            >
              <option value="en">English</option>
              <option value="fr">French</option>
            </select>
          </label>

          <label>
            Body
            <textarea
              rows={8}
              value={formValues.body}
              onChange={(event) => updateField('body', event.target.value)}
            />
          </label>

          <div className="editor-form-group">
            <h4>Article sections</h4>
            <p>Add paragraph blocks with optional subheadlines and uploaded images.</p>
            {(formValues.articleSections || []).map((section, index) => (
              <div key={`article-section-${index}`} className="editor-list-item">
                <label>
                  Section type
                  <select
                    value={section.contentType}
                    onChange={(event) =>
                      updateListItem(
                        'articleSections',
                        index,
                        'contentType',
                        event.target.value
                      )
                    }
                  >
                    <option value="Text">Text</option>
                    <option value="Link">Link</option>
                    <option value="Quote">Quote</option>
                  </select>
                </label>

                <label>
                  Section title
                  <input
                    value={section.subHeadline}
                    onChange={(event) =>
                      updateListItem(
                        'articleSections',
                        index,
                        'subHeadline',
                        event.target.value
                      )
                    }
                  />
                </label>

                <label>
                  Paragraph/body
                  <textarea
                    rows={5}
                    value={section.body}
                    onChange={(event) =>
                      updateListItem('articleSections', index, 'body', event.target.value)
                    }
                  />
                </label>

                <label>
                  Section image upload
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      handleArticleSectionImageChange(index, event)
                    }
                  />
                </label>

                {files.articleSectionImageFiles?.[index] ? (
                  <p>Selected image: {files.articleSectionImageFiles[index].name}</p>
                ) : null}

                {section.imageUrl ? (
                  <label>
                    Current section image URL (read-only)
                    <input value={section.imageUrl} readOnly />
                  </label>
                ) : null}

                <label>
                  Image caption
                  <input
                    value={section.imageCaption}
                    onChange={(event) =>
                      updateListItem(
                        'articleSections',
                        index,
                        'imageCaption',
                        event.target.value
                      )
                    }
                  />
                </label>

                <button
                  type="button"
                  className="danger"
                  onClick={() => removeListItem('articleSections', index)}
                >
                  Remove section
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() =>
                addListItem('articleSections', { ...EMPTY_ARTICLE_SECTION })
              }
            >
              Add section
            </button>
          </div>

          <div className="editor-form-group">
            <h4>References (bibliography)</h4>
            {(formValues.articleReferences || []).map((reference, index) => (
              <div key={`article-reference-${index}`} className="editor-list-item">
                <label>
                  Reference text
                  <input
                    value={reference.text}
                    onChange={(event) =>
                      updateListItem(
                        'articleReferences',
                        index,
                        'text',
                        event.target.value
                      )
                    }
                  />
                </label>

                <label>
                  Reference link
                  <input
                    value={reference.link}
                    onChange={(event) =>
                      updateListItem(
                        'articleReferences',
                        index,
                        'link',
                        event.target.value
                      )
                    }
                    placeholder="https://..."
                  />
                </label>

                <button
                  type="button"
                  className="danger"
                  onClick={() => removeListItem('articleReferences', index)}
                >
                  Remove reference
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                addListItem('articleReferences', { ...EMPTY_ARTICLE_REFERENCE })
              }
            >
              Add reference
            </button>
          </div>

          <div className="editor-form-group">
            <h4>Footnotes</h4>
            {(formValues.articleFootnotes || []).map((footnote, index) => (
              <div key={`article-footnote-${index}`} className="editor-list-item">
                <label>
                  Footnote number
                  <input
                    type="number"
                    min="1"
                    value={footnote.footnoteNumber}
                    onChange={(event) =>
                      updateListItem(
                        'articleFootnotes',
                        index,
                        'footnoteNumber',
                        event.target.value
                      )
                    }
                  />
                </label>

                <label>
                  Footnote text
                  <input
                    value={footnote.text}
                    onChange={(event) =>
                      updateListItem('articleFootnotes', index, 'text', event.target.value)
                    }
                  />
                </label>

                <label>
                  Footnote link
                  <input
                    value={footnote.link}
                    onChange={(event) =>
                      updateListItem('articleFootnotes', index, 'link', event.target.value)
                    }
                    placeholder="https://..."
                  />
                </label>

                <button
                  type="button"
                  className="danger"
                  onClick={() => removeListItem('articleFootnotes', index)}
                >
                  Remove footnote
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                addListItem('articleFootnotes', { ...EMPTY_ARTICLE_FOOTNOTE })
              }
            >
              Add footnote
            </button>
          </div>

          <label>
            Cover image
            <input
              type="file"
              accept="image/*"
              onChange={(event) => handleFileChange('coverImageFile', event)}
            />
          </label>

          <label>
            Related media
            <input
              type="file"
              multiple
              onChange={(event) =>
                handleFileChange('relatedMediaFiles', event, true)
              }
            />
          </label>
        </>
      ) : null}

      {collectionKey === 'animations' ? (
        <>
          <label>
            Topic
            <input
              value={formValues.topic}
              onChange={(event) => updateField('topic', event.target.value)}
            />
          </label>

          <label>
            Creator name
            <input
              value={formValues.creatorName}
              onChange={(event) => updateField('creatorName', event.target.value)}
            />
          </label>

          <label>
            Creator discipline
            <input
              value={formValues.creatorDiscipline}
              onChange={(event) =>
                updateField('creatorDiscipline', event.target.value)
              }
            />
          </label>

          <label>
            Creator statement
            <textarea
              rows={4}
              value={formValues.creatorStatement}
              onChange={(event) =>
                updateField('creatorStatement', event.target.value)
              }
            />
          </label>

          <label>
            Media type
            <select
              value={formValues.mediaType || ''}
              onChange={(event) => updateField('mediaType', event.target.value)}
            >
              <option value="">Not set</option>
              {GALLERY_MEDIA_TYPE_OPTIONS.map((mediaType) => (
                <option key={mediaType} value={mediaType}>
                  {mediaType}
                </option>
              ))}
              {formValues.mediaType &&
              !GALLERY_MEDIA_TYPE_OPTIONS.includes(formValues.mediaType) ? (
                <option value={formValues.mediaType}>{formValues.mediaType}</option>
              ) : null}
            </select>
          </label>

          <label>
            Description
            <textarea
              rows={6}
              value={formValues.description}
              onChange={(event) => updateField('description', event.target.value)}
            />
          </label>

          <label>
            Primary media
            <input
              type="file"
              onChange={(event) => handleFileChange('mediaFile', event)}
            />
          </label>

          <label>
            Thumbnail
            <input
              type="file"
              accept="image/*"
              onChange={(event) => handleFileChange('thumbnailFile', event)}
            />
          </label>
        </>
      ) : null}

      {collectionKey === 'models' ? (
        <>
          <label>
            Model type
            <input
              value={formValues.type}
              onChange={(event) => updateField('type', event.target.value)}
              placeholder="example: workshop-model"
            />
          </label>

          <label>
            Display order
            <input
              type="number"
              min="1"
              value={formValues.sortOrder}
              onChange={(event) => updateField('sortOrder', event.target.value)}
              placeholder="1"
            />
          </label>

          <label>
            Description
            <textarea
              rows={6}
              value={formValues.description}
              onChange={(event) => updateField('description', event.target.value)}
            />
          </label>

          <label>
            Metadata (JSON)
            <textarea
              rows={6}
              value={formValues.metadata}
              onChange={(event) => updateField('metadata', event.target.value)}
            />
          </label>

      <label>
            Preview image
            <input
              type="file"
              accept="image/*"
              onChange={(event) => handleFileChange('previewImageFile', event)}
            />
          </label>

          <label>
            Course video
            <input
              type="file"
              accept="video/*"
              onChange={(event) => handleFileChange('courseVideoFile', event)}
            />
          </label>

          <label>
            Attachments
            <input
              type="file"
              multiple
              onChange={(event) =>
                handleFileChange('attachmentFiles', event, true)
              }
            />
          </label>
        </>
      ) : null}

      {initialContent ? (
        <div className="existing-media">
          <p>Existing media references</p>
          <ul>
            {initialContent.coverImagePath ? (
              <li>Cover: {initialContent.coverImagePath}</li>
            ) : null}
            {initialContent.mediaPath ? <li>Media: {initialContent.mediaPath}</li> : null}
            {initialContent.thumbnailPath ? (
              <li>Thumbnail: {initialContent.thumbnailPath}</li>
            ) : null}
            {initialContent.previewImagePath ? (
              <li>Preview: {initialContent.previewImagePath}</li>
            ) : null}
            {initialContent.videoPath ? <li>Video: {initialContent.videoPath}</li> : null}
            {Array.isArray(initialContent.relatedMedia) &&
            initialContent.relatedMedia.length > 0 ? (
              <li>Related media items: {initialContent.relatedMedia.length}</li>
            ) : null}
            {Array.isArray(initialContent.articleSections) &&
            initialContent.articleSections.length > 0 ? (
              <li>Article sections: {initialContent.articleSections.length}</li>
            ) : null}
            {Array.isArray(initialContent.articleReferences) &&
            initialContent.articleReferences.length > 0 ? (
              <li>References: {initialContent.articleReferences.length}</li>
            ) : null}
            {Array.isArray(initialContent.articleFootnotes) &&
            initialContent.articleFootnotes.length > 0 ? (
              <li>Footnotes: {initialContent.articleFootnotes.length}</li>
            ) : null}
            {Array.isArray(initialContent.attachments) &&
            initialContent.attachments.length > 0 ? (
              <li>Attachments: {initialContent.attachments.length}</li>
            ) : null}
          </ul>
        </div>
      ) : null}

      <button type="submit" disabled={isSaving}>
        {isSaving
          ? 'Saving...'
          : `${initialContent ? 'Update' : 'Create'} ${config?.singular}`}
      </button>
    </form>
  );
}
